import { useMsal } from "@azure/msal-react";
import {
	AccountInfo,
	AuthenticationResult,
	IPublicClientApplication,
	InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { Method } from "axios";
import { api_s2 } from "../config";

/**
 * Minimal server-sent-events payload the caller supplies. Mirrors the subset of
 * the @microsoft/fetch-event-source options the streaming agent relies on, so we
 * can stream over native fetch without pulling in that dependency.
 */
export interface EventSourcePrompt {
	body: string;
	headers?: Record<string, string>;
	onmessage?: (ev: { data: string; id?: string }) => void;
	onclose?: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onerror?: (err: any) => void;
}

/**
 * Acquire an S2 access token and POST to an SSE endpoint, parsing the streamed
 * `data:` frames and dispatching them to the supplied callbacks.
 *
 * Adapted from my-pymble-portal's usePymbleEventSource, using this app's
 * @azure/msal-react auth and a native fetch + ReadableStream reader (the portal
 * uses @microsoft/fetch-event-source, which isn't a dependency here).
 */
export const callPymbleEventSource = async (
	instance: IPublicClientApplication,
	method: Method,
	url: string,
	prompt: EventSourcePrompt
): Promise<void> => {
	await instance.initialize();

	let account: AccountInfo | null = null;
	try {
		account = instance.getAllAccounts()[0] || null;
	} catch (error) {
		prompt.onerror?.(error);
		prompt.onclose?.();
		return;
	}

	if (!account) {
		prompt.onerror?.("No account found");
		prompt.onclose?.();
		return;
	}

	let auth: AuthenticationResult | void;
	try {
		auth = await instance.acquireTokenSilent({ scopes: api_s2.scopes, account });
	} catch (error) {
		if (error instanceof InteractionRequiredAuthError) {
			await instance.acquireTokenRedirect({ scopes: api_s2.scopes, account });
			return;
		}
		prompt.onerror?.(error);
		prompt.onclose?.();
		return;
	}

	if (!auth?.accessToken) {
		prompt.onerror?.("No access token found");
		prompt.onclose?.();
		return;
	}

	try {
		const response = await fetch(url, {
			method,
			headers: {
				Accept: "text/event-stream",
				PymbleClientId: "pastoral-admin",
				Authorization: `Bearer ${auth.accessToken}`,
				...prompt.headers,
			},
			body: prompt.body,
		});

		if (!response.ok || !response.body) {
			prompt.onerror?.(`Stream request failed (${response.status})`);
			prompt.onclose?.();
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		// SSE frames are separated by a blank line; each frame may carry one or
		// more `data:` lines. We emit one onmessage per data line, matching the
		// portal's per-chunk handling.
		const flushFrame = (frame: string) => {
			const dataLines = frame
				.split("\n")
				.filter((line) => line.startsWith("data:"))
				.map((line) => line.slice(5).replace(/^ /, ""));
			if (dataLines.length === 0) return;
			const data = dataLines.join("\n");
			if (data === "[DONE]") return;
			prompt.onmessage?.({ data });
		};

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			let sepIndex: number;
			while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
				const frame = buffer.slice(0, sepIndex);
				buffer = buffer.slice(sepIndex + 2);
				flushFrame(frame);
			}
		}
		// Flush any trailing frame with no closing blank line.
		if (buffer.trim().length > 0) flushFrame(buffer);

		prompt.onclose?.();
	} catch (error) {
		prompt.onerror?.(error);
		prompt.onclose?.();
	}
};

export function usePymbleEventSource() {
	const { instance } = useMsal();

	const callEventSource = async (
		method: Method,
		url: string,
		prompt: EventSourcePrompt
	) => {
		return callPymbleEventSource(instance, method, url, prompt);
	};

	return { callEventSource };
}
